import { useState } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { MagnifyingGlass } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { BetterDocPreferredCareProviderSearchDialog } from "@/modules/patients/detail/better-doc/de/components/better-doc-preferred-care-provider/better-doc-preferred-care-provider-search-dialog"
import { BetterDocPreferredCareProviderValue } from "@/modules/patients/detail/better-doc/de/components/better-doc-preferred-care-provider/better-doc-preferred-care-provider-value"
import type { BetterDocCareProviderSearchItem } from "@/modules/patients/detail/better-doc/de/services/betterDocCareProviderSearchQuery"
import type { BetterDocSearchBaseFormType } from "@/modules/patients/detail/better-doc/schema/better-doc-search-base-schema"
import type { PointOfCareType } from "@/types/__generated__/union-types"

type BetterDocPreferredCareProviderSectionProps = {
  progressNoteId: string
  effectivePointOfCare: PointOfCareType
  specialtyId: string | null
  selectedCareProvider: BetterDocCareProviderSearchItem | null
  onSelect: (careProvider: BetterDocCareProviderSearchItem) => void
  onRemove: () => void
}

export const BetterDocPreferredCareProviderSection = ({
  progressNoteId,
  effectivePointOfCare,
  specialtyId,
  selectedCareProvider,
  onSelect,
  onRemove,
}: BetterDocPreferredCareProviderSectionProps) => {
  const { t } = useTranslation()
  const { trigger } = useFormContext<BetterDocSearchBaseFormType>()
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState(false)

  const openSearchDialogWhenConsultationReasonIsChosen = async () => {
    const isConsultationReasonChosen = await trigger("reasonForConsultations")

    if (isConsultationReasonChosen) {
      setIsSearchDialogOpen(true)
    }
  }

  const openSearchDialog = () => void openSearchDialogWhenConsultationReasonIsChosen()

  const closeSearchDialog = () => {
    setIsSearchDialogOpen(false)
  }

  return (
    <div className="grid w-full gap-4" data-testid="better-doc-preferred-care-provider-section">
      <div className="text-on-surface text-base leading-6 font-medium">
        {t("episodeOfCare.betterDoc.preferredCareProvider.title")}
      </div>
      {selectedCareProvider ? (
        <BetterDocPreferredCareProviderValue
          careProvider={selectedCareProvider}
          onOpenSearch={openSearchDialog}
          onRemove={onRemove}
        />
      ) : (
        <Button
          type="button"
          variant="secondary"
          className="w-full justify-start"
          data-testid="better-doc-preferred-care-provider-open-search-button"
          onClick={openSearchDialog}>
          <MagnifyingGlass className="size-4" />
          {t("episodeOfCare.betterDoc.preferredCareProvider.openSearch")}
        </Button>
      )}
      {isSearchDialogOpen && (
        <BetterDocPreferredCareProviderSearchDialog
          progressNoteId={progressNoteId}
          effectivePointOfCare={effectivePointOfCare}
          specialtyId={specialtyId}
          onSelect={onSelect}
          onClose={closeSearchDialog}
        />
      )}
    </div>
  )
}
